import { Controller } from "@hotwired/stimulus"

// Tasks::TagEditorComponent — chip-list tag editor.
//
// Maintains a single source of truth in the hidden `field` target as
// the comma-separated `tags_input` the controller already accepts.
// Adding a chip: type a value into the trailing input and commit on
// Enter or comma; commit also fires on blur so a typed-but-not-pressed
// value isn't lost. Removing a chip: click the × on its label.
export default class extends Controller {
  static targets = ["field", "chips", "chip", "input"]
  static values = { tags: { type: Array, default: [] } }

  connect() {
    if (!this.tagsValue.length && this.hasFieldTarget && this.fieldTarget.value) {
      this.tagsValue = this.fieldTarget.value.split(",").map((tag) => tag.trim()).filter(Boolean)
    }
    this.serialise()
  }

  onKey(event) {
    if (event.key === "Enter" || event.key === ",") {
      event.preventDefault()
      this.commit()
    } else if (event.key === "Backspace" && event.currentTarget.value === "" && this.tagsValue.length) {
      event.preventDefault()
      this.tagsValue = this.tagsValue.slice(0, -1)
      this.render()
    }
  }

  commit() {
    if (!this.hasInputTarget) return
    const candidate = this.inputTarget.value.trim().replace(/,/g, "")
    if (!candidate) return
    if (!this.tagsValue.includes(candidate)) {
      this.tagsValue = [...this.tagsValue, candidate]
    }
    this.inputTarget.value = ""
    this.render()
  }

  remove(event) {
    const value = event.currentTarget.dataset.value
    this.tagsValue = this.tagsValue.filter((tag) => tag !== value)
    this.render()
  }

  render() {
    if (!this.hasChipsTarget) return
    this.chipTargets.forEach((chip) => chip.remove())
    const fragment = document.createDocumentFragment()
    this.tagsValue.forEach((tag) => fragment.appendChild(this.buildChip(tag)))
    this.chipsTarget.insertBefore(fragment, this.inputTarget)
    this.serialise()
  }

  serialise() {
    if (this.hasFieldTarget) this.fieldTarget.value = this.tagsValue.join(",")
  }

  buildChip(tag) {
    const chip = document.createElement("span")
    chip.className = "tasks-tag-editor__chip inline-flex items-center gap-1 rounded-full border border-stone-300 bg-stone-100 px-2 py-0.5 text-[11px] text-stone-700"
    chip.dataset["tasks-TagEditorTarget"] = "chip"
    chip.dataset.value = tag
    chip.setAttribute("data-tasks--tag-editor-target", "chip")

    const label = document.createElement("span")
    label.textContent = tag
    chip.appendChild(label)

    const remove = document.createElement("button")
    remove.type = "button"
    remove.className = "tasks-tag-editor__remove text-stone-500 hover:text-stone-800"
    remove.setAttribute("aria-label", `Remove tag ${tag}`)
    remove.setAttribute("data-action", "click->tasks--tag-editor#remove")
    remove.dataset.value = tag
    remove.innerHTML = "&times;"
    chip.appendChild(remove)

    return chip
  }
}
